import React from 'react';
import { money, fmtDate } from '../../utils/crmApi';

// ════════════════════════════════════════════════════════════════════════════
//  DOCUMENT PREVIEW  —  SWAPPABLE TEMPLATE
//
//  This is the on-screen preview of a quote / sales order / invoice and the
//  basis for the PDF that will be emailed to the customer.
//
//  ▸ The customer's branded template will be dropped in HERE later.
//    Only this component needs to change — the data shape (the `doc` prop) and
//    the rest of the CRM (workflow, totals, emailing) stay exactly the same.
//
//  `doc` contains: doc_type, doc_number, status, issue_date, due_date,
//  account_name/address/email/phone/tax_id, items[], subtotal, discount_type,
//  discount_value, discount_amount, tax_rate, tax_amount, total, notes, terms.
// ════════════════════════════════════════════════════════════════════════════

const TYPE_LABEL = { quote: 'QUOTE', sales_order: 'SERVICE ORDER', invoice: 'INVOICE' };

export default function DocumentPreview({ doc }) {
  const label = TYPE_LABEL[doc.doc_type] || 'DOCUMENT';
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ background: '#fff', color: '#181C2A', padding: '34px 40px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '30px' }}>
          <div>
            {/* Company block — replace with your letterhead later */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: 40, height: 40, borderRadius: '10px', background: '#0D9488', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 18 }}>C</div>
              <div>
                <div style={{ fontSize: '18px', fontWeight: '800' }}>Your Company</div>
                <div style={{ fontSize: '11px', color: '#8A93A6' }}>Muscat, Oman</div>
              </div>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '26px', fontWeight: '800', letterSpacing: '1px', color: '#0D9488' }}>{label}</div>
            <div style={{ fontSize: '13px', fontWeight: '700', fontFamily: 'monospace', marginTop: '2px' }}>{doc.doc_number}</div>
            <div style={{ fontSize: '11px', color: '#8A93A6', marginTop: '6px' }}>Issued: {fmtDate(doc.issue_date)}</div>
            {doc.due_date && <div style={{ fontSize: '11px', color: '#8A93A6' }}>Due: {fmtDate(doc.due_date)}</div>}
          </div>
        </div>

        {/* Bill to */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '26px' }}>
          <div>
            <div style={{ fontSize: '10px', fontWeight: '700', color: '#8A93A6', textTransform: 'uppercase', letterSpacing: '.7px', marginBottom: '5px' }}>Bill To</div>
            <div style={{ fontSize: '14px', fontWeight: '700' }}>{doc.account_name || '—'}</div>
            {doc.account_address && <div style={{ fontSize: '12px', color: '#5b6478' }}>{doc.account_address}</div>}
            {(doc.account_city || doc.account_country) && <div style={{ fontSize: '12px', color: '#5b6478' }}>{[doc.account_city, doc.account_country].filter(Boolean).join(', ')}</div>}
            {doc.account_email && <div style={{ fontSize: '12px', color: '#5b6478' }}>{doc.account_email}</div>}
            {doc.account_tax_id && <div style={{ fontSize: '12px', color: '#5b6478' }}>Tax ID: {doc.account_tax_id}</div>}
          </div>
          {doc.contact && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '10px', fontWeight: '700', color: '#8A93A6', textTransform: 'uppercase', letterSpacing: '.7px', marginBottom: '5px' }}>Attn</div>
              <div style={{ fontSize: '13px', fontWeight: '600' }}>{doc.contact.first_name} {doc.contact.last_name}</div>
              {doc.contact.email && <div style={{ fontSize: '12px', color: '#5b6478' }}>{doc.contact.email}</div>}
            </div>
          )}
        </div>

        {/* Items table */}
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #181C2A' }}>
              <th style={{ textAlign: 'left', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '.6px', color: '#8A93A6', padding: '8px 6px' }}>Description</th>
              <th style={{ textAlign: 'right', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '.6px', color: '#8A93A6', padding: '8px 6px', width: '70px' }}>Qty</th>
              <th style={{ textAlign: 'left', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '.6px', color: '#8A93A6', padding: '8px 6px', width: '60px' }}>Unit</th>
              <th style={{ textAlign: 'right', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '.6px', color: '#8A93A6', padding: '8px 6px', width: '120px' }}>Unit Price</th>
              <th style={{ textAlign: 'right', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '.6px', color: '#8A93A6', padding: '8px 6px', width: '120px' }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {doc.items?.map(it => (
              <tr key={it.id} style={{ borderBottom: '1px solid #E4E7EF' }}>
                <td style={{ padding: '9px 6px', fontSize: '13px' }}>
                  <div style={{ fontWeight: '600' }}>{it.name}</div>
                  {it.description && <div style={{ fontSize: '11px', color: '#8A93A6' }}>{it.description}</div>}
                  {(() => {
                    const base = Number(it.unit_price) || 0, val = Number(it.discount_value) || 0;
                    if (it.discount_type === 'amount') return <div style={{ fontSize: '11px', color: '#DC2626' }}>Discount: {val.toFixed(3)}/unit</div>;
                    if (it.discount_type === 'percent') return <div style={{ fontSize: '11px', color: '#DC2626' }}>Discount: {val}%</div>;
                    if (it.discount_type === 'inc_amount') return <div style={{ fontSize: '11px', color: '#16A34A' }}>Increase: {val.toFixed(3)}/unit</div>;
                    if (it.discount_type === 'inc_percent') return <div style={{ fontSize: '11px', color: '#16A34A' }}>Increase: {val}%</div>;
                    return null;
                  })()}
                </td>
                <td style={{ padding: '9px 6px', fontSize: '13px', textAlign: 'right' }}>{Number(it.quantity)}</td>
                <td style={{ padding: '9px 6px', fontSize: '12px', color: '#5b6478' }}>{it.unit}</td>
                <td style={{ padding: '9px 6px', fontSize: '13px', textAlign: 'right' }}>{(Number(it.quantity) ? Number(it.line_total) / Number(it.quantity) : Number(it.unit_price)).toFixed(3)}</td>
                <td style={{ padding: '9px 6px', fontSize: '13px', textAlign: 'right', fontWeight: '600' }}>{Number(it.line_total).toFixed(3)}</td>
              </tr>
            ))}
            {(!doc.items || doc.items.length === 0) && (
              <tr><td colSpan={5} style={{ padding: '16px', textAlign: 'center', color: '#8A93A6', fontSize: '13px' }}>No line items.</td></tr>
            )}
          </tbody>
        </table>

        {/* Totals */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ width: '280px' }}>
            <TotalRow label="Subtotal" value={money(doc.subtotal, doc.currency)} />
            {Number(doc.discount_amount) > 0 && (
              <TotalRow label={`Discount${doc.discount_type === 'percent' ? ` (${Number(doc.discount_value)}%)` : ''}`} value={`− ${money(doc.discount_amount, doc.currency)}`} color="#DC2626" />
            )}
            {Number(doc.tax_amount) > 0 && <TotalRow label={`Tax (${Number(doc.tax_rate)}%)`} value={money(doc.tax_amount, doc.currency)} />}
            <div style={{ borderTop: '2px solid #181C2A', marginTop: '6px', paddingTop: '6px' }}>
              <TotalRow label="Total" value={money(doc.total, doc.currency)} bold />
            </div>
          </div>
        </div>

        {/* Notes / terms */}
        {(doc.notes || doc.terms) && (
          <div style={{ marginTop: '28px', borderTop: '1px solid #E4E7EF', paddingTop: '14px' }}>
            {doc.notes && <div style={{ marginBottom: '10px' }}><div style={{ fontSize: '10px', fontWeight: '700', color: '#8A93A6', textTransform: 'uppercase' }}>Notes</div><div style={{ fontSize: '12px', color: '#5b6478' }}>{doc.notes}</div></div>}
            {doc.terms && <div><div style={{ fontSize: '10px', fontWeight: '700', color: '#8A93A6', textTransform: 'uppercase' }}>Terms</div><div style={{ fontSize: '12px', color: '#5b6478' }}>{doc.terms}</div></div>}
          </div>
        )}
      </div>
    </div>
  );
}

function TotalRow({ label, value, bold, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: bold ? '17px' : '13px', fontWeight: bold ? '800' : '500' }}>
      <span style={{ color: '#5b6478' }}>{label}</span>
      <span style={{ color: color || '#181C2A' }}>{value}</span>
    </div>
  );
}
